import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Moon, Sun } from "lucide-react";
import { api } from "@/lib/api";
import "./budsjettrapport.css";

const nk = (n) => Math.round(Number(n) || 0).toLocaleString("en-GB");
const mn = (n) => `${((Number(n) || 0) / 1e6).toFixed(2)}m`;
const kmp = (n) => (Math.abs(Number(n) || 0) >= 1e6 ? mn(n) : `${Math.round((Number(n) || 0) / 1000)}k`);
const MND = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mnd = (m) => (m ? `${MND[parseInt(m.slice(5, 7), 10) - 1]} ${m.slice(2, 4)}` : "—");

const NAV = [
  ["bud-pnl", "01", "P&L"],
  ["bud-engines", "02", "Engines"],
  ["bud-costs", "03", "Costs"],
  ["bud-scenarios", "04", "Scenarios"],
  ["bud-cash", "05", "Liquidity"],
  ["bud-partners", "06", "Partners"],
  ["bud-roadmap", "07", "Roadmap"],
  ["bud-risks", "08", "Risks"],
];

const Head = ({ idx, lbl }) => (
  <div className="bud-head">
    <span className="idx">{idx}</span>
    <span className="lbl">{lbl}</span>
    <span className="rule" />
  </div>
);

function Rapport({ d, chrome, lys }) {
  const c = d.computed;
  const b = c.scenarios.base;
  const [aktiv, setAktiv] = useState("bud-pnl");
  const rotRef = useRef(null);

  // Reveal-koreografi + scrollspy — print-CSS tvinger alt synlig
  useEffect(() => {
    const rot = rotRef.current;
    if (!rot) return undefined;
    const els = Array.from(rot.querySelectorAll(".bud-reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      els.forEach((el) => el.classList.add("in"));
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
      }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
      els.forEach((el) => io.observe(el));
      const spy = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setAktiv(e.target.getAttribute("data-testid")); });
      }, { rootMargin: "-30% 0px -55% 0px" });
      NAV.forEach(([id]) => { const el = rot.querySelector(`[data-testid="${id}"]`); if (el) spy.observe(el); });
      return () => { io.disconnect(); spy.disconnect(); };
    }
    return undefined;
  }, []);

  const dato = d.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(d.eventDate)
    ? new Date(`${d.eventDate}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "13 April 2027";
  const venue = (d.venue || "Grieghallen, Bergen").split(",")[0].trim();
  const genererte = (d.generatedAt || "").slice(0, 10);
  const paid = b.physical.sold;
  const yieldPer = paid > 0 ? Math.round(b.revenue.physical / paid) : 0;
  const targetTotal = c.sponsorSummary.target || 0;
  const signert = c.sponsorSummary.signed || 0;
  const vektet = c.sponsorSummary.weightedPipeline || 0;
  const scenKeys = ["downside", "base", "upside"];
  const linjerPrKat = (kat) => (d.lines || []).filter((l) => l.category === kat);
  const katSum = (kat) => linjerPrKat(kat).reduce((a, l) => a + (l.forecastBase || 0), 0);
  const maxCash = Math.max(1, ...b.cash.months.map((m) => Math.abs(m.closing)));
  const maxProfit = Math.max(1, ...scenKeys.map((k) => Math.abs(c.scenarios[k].profit)));
  const mixDeler = [
    ["Physical", b.revenue.physical, "var(--acc)"],
    ["Partners", b.revenue.sponsors, "var(--mix2)"],
    ["Digital", b.revenue.digital, "var(--mix3)"],
    ["Dinner", b.revenue.dinner, "var(--mix4)"],
  ];
  const beReq = b.breakEven.physicalTicketsRequired;
  const sellable = b.physical.sellable || 1;
  const hoppTil = (id) => rotRef.current?.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className={`bud-root${lys ? " bud-lys" : ""}`} data-testid="budsjett-rapport" ref={rotRef}>
      {chrome}

      {/* Scrollspy-nav — kun bred skjerm, aldri print */}
      <nav className="bud-nav bud-chrome" aria-label="Sections">
        {NAV.map(([id, idx, lbl]) => (
          <button key={id} type="button" className={aktiv === id ? "aktiv" : ""} onClick={() => hoppTil(id)}>
            <span className="strek" />{idx} {lbl}
          </button>
        ))}
      </nav>

      {/* Gjentakende footer på hver print-side */}
      <div className="bud-pagefoot" aria-hidden="true">
        <span>Norwegian AI Summit 2027 — Budget memo</span>
        <span>Private · not for distribution</span>
      </div>

      <div className="bud-doc">
        {/* ── Cover ── */}
        <header className="bud-cover">
          <div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="wordmark" style={{ fontSize: 13 }}>
                <span className="wordmark__bold">Norwegian</span>
                <span className="wordmark__light">AI Summit</span>
              </span>
              <span className="bud-chip">Budget memo — Private</span>
            </div>
            <h1 className="bud-h1">The<br />Economics<span className="bud-gul">.</span></h1>
            <p className="bud-cover-sub">
              First-year operating budget for the premium one-day AI summit — deliberately limited,
              built around world-class content, access and an exceptional live experience.
            </p>
          </div>
          <div>
            <div className="bud-meta">
              {[["Event", dato], ["Venue", `${venue} · Bergen`], ["Basis", "Base Case · NOK ex VAT"], ["Generated", genererte]].map(([k, v]) => (
                <div key={k}><p className="k">{k}</p><p className="v">{v}</p></div>
              ))}
            </div>
            <div className="bud-hero">
              <div className="bud-stat"><p className="v">{mn(b.revenue.total)}</p><p className="k">Revenue forecast</p><p className="s">{paid} paid · {b.digital.sold} digital</p></div>
              <div className="bud-stat"><p className="v">{mn(b.costs.total)}</p><p className="k">Cost forecast</p><p className="s">incl. {kmp(b.costs.contingency)} contingency</p></div>
              <div className="bud-stat"><p className="v" style={{ color: b.profit < 0 ? "var(--neg)" : "var(--pos)" }}>{b.profit < 0 ? "−" : "+"}{mn(Math.abs(b.profit))}</p><p className="k">Forecast profit</p><p className="s">{b.margin}% margin</p></div>
              <div className="bud-stat"><p className="v">{beReq ?? "—"}</p><p className="k">Break-even</p><p className="s">of {sellable} paid tickets</p></div>
            </div>
            <div className="bud-mix">
              <div className="bud-mix-bar">
                {mixDeler.map(([k, v, farge]) => (
                  <span key={k} style={{ width: `${(v / Math.max(1, b.revenue.total)) * 100}%`, background: farge }} title={`${k} ${kmp(v)}`} />
                ))}
              </div>
              <div className="bud-legend">
                {mixDeler.map(([k, v, farge]) => (
                  <span key={k} className="li"><span className="pk" style={{ background: farge }} />{k} {kmp(v)}</span>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* 01 P&L */}
        <section className="bud-sek bud-reveal bud-pagebreak" data-testid="bud-pnl">
          <Head idx="01" lbl="P&L — Base case" />
          <table className="bud-tabell">
            <thead><tr><th>Revenue</th><th className="r">NOK ex VAT</th></tr></thead>
            <tbody>
              <tr><td>Physical tickets — {paid} paid</td><td className="r">{nk(b.revenue.physical)}</td></tr>
              <tr><td>Summit Dinner add-ons</td><td className="r">{nk(b.revenue.dinner)}</td></tr>
              <tr><td>Digital participation</td><td className="r">{nk(b.revenue.digital)}</td></tr>
              <tr><td>Partnerships{b.revenue.sponsorsBasis === "assumption" ? " — planning assumption" : ""}</td><td className="r">{nk(b.revenue.sponsors)}</td></tr>
              {b.revenue.other > 0 && <tr><td>Other revenue</td><td className="r">{nk(b.revenue.other)}</td></tr>}
              <tr className="tot"><td>Total revenue</td><td className="r">{nk(b.revenue.total)}</td></tr>
              <tr className="dus"><td>Total costs incl. {d.contingencyPct}% contingency</td><td className="r">−{nk(b.costs.total)}</td></tr>
              <tr className="tot"><td>Forecast profit — {b.margin}% margin</td><td className="r" style={{ color: b.profit < 0 ? "var(--neg)" : "var(--pos)" }}>{b.profit < 0 ? "−" : "+"}{nk(Math.abs(b.profit))}</td></tr>
            </tbody>
          </table>
        </section>

        {/* 02 Revenue engines */}
        <section className="bud-sek bud-reveal" data-testid="bud-engines">
          <Head idx="02" lbl="Revenue engines" />
          <table className="bud-tabell">
            <thead><tr><th>Product</th><th className="r">Price</th><th className="r">Sold (Base)</th><th className="r">Revenue</th></tr></thead>
            <tbody>
              {(d.tickets || []).map((t) => (
                <tr key={t.name} className={t.soldBase === 0 ? "dus" : ""}>
                  <td>{t.name}{t.channel !== "physical" ? ` — ${t.channel}` : ""}</td>
                  <td className="r">{nk(t.price)}</td>
                  <td className="r">{t.soldBase} / {t.capacity}</td>
                  <td className="r">{nk(t.revenueBase)}</td>
                </tr>
              ))}
              <tr className="tot"><td>Blended physical yield</td><td className="r" colSpan={3}>~{nk(yieldPer)} per paid participant</td></tr>
            </tbody>
          </table>
        </section>

        {/* 03 Cost budget */}
        <section className="bud-sek stor bud-reveal bud-pagebreak" data-testid="bud-costs">
          <Head idx="03" lbl="Cost budget — Base case" />
          {(c.categories || []).map((kat) => (
            <div key={kat} className="bud-kat">
              <div className="flex items-baseline justify-between gap-3">
                <p className="bud-katnavn">{(c.categoryLabels || {})[kat] || kat}</p>
                <p className="bud-mono bud-tab text-[10px]" style={{ color: "var(--ink)" }}>
                  {nk(katSum(kat))} <span style={{ color: "var(--dim)" }}>· {Math.round((katSum(kat) / Math.max(1, b.costs.subtotal)) * 100)}%</span>
                </p>
              </div>
              <div className="bud-kat-bar"><span style={{ width: `${(katSum(kat) / Math.max(1, b.costs.subtotal)) * 100}%` }} /></div>
              <table className="bud-tabell">
                <tbody>
                  {linjerPrKat(kat).map((l) => (
                    <tr key={l.name}>
                      <td>{l.name}</td>
                      <td className="r" style={{ width: 90 }}><span className={`bud-status ${l.status}`}>{l.status}</span></td>
                      <td className="r" style={{ width: 110 }}>{nk(l.forecastBase)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <table className="bud-tabell">
            <tbody>
              <tr className="dus"><td>Subtotal</td><td className="r">{nk(b.costs.subtotal)}</td></tr>
              <tr className="dus"><td>Contingency — {d.contingencyPct}%</td><td className="r">{nk(b.costs.contingency)}</td></tr>
              <tr className="tot"><td>Total base cost</td><td className="r">{nk(b.costs.total)}</td></tr>
            </tbody>
          </table>
        </section>

        {/* 04 Scenarios */}
        <section className="bud-sek bud-reveal bud-pagebreak" data-testid="bud-scenarios">
          <Head idx="04" lbl="Scenarios" />
          <div className="bud-scen-viz">
            {scenKeys.map((k) => {
              const s = c.scenarios[k];
              const neg = s.profit < 0;
              return (
                <div className="kol" key={k}>
                  <p className="nm">{k}</p>
                  <p className="pf" style={{ color: neg ? "var(--neg)" : "var(--pos)" }}>{neg ? "−" : "+"}{kmp(Math.abs(s.profit))}</p>
                  <div className="spor"><span style={{ width: `${(Math.abs(s.profit) / maxProfit) * 100}%`, background: neg ? "var(--neg)" : "var(--pos)" }} /></div>
                  <p className="sub">{s.physical.sold} paid · {kmp(s.revenue.total)} rev · {kmp(s.costs.total)} cost</p>
                </div>
              );
            })}
          </div>
          <table className="bud-tabell">
            <thead>
              <tr><th></th>{scenKeys.map((k) => <th key={k} className={`r${k === "base" ? " base" : ""}`}>{k.charAt(0).toUpperCase() + k.slice(1)}</th>)}</tr>
            </thead>
            <tbody>
              {[
                ["Paid physical", (s) => s.physical.sold],
                ["Physical revenue", (s) => kmp(s.revenue.physical)],
                ["Partner revenue", (s) => kmp(s.revenue.sponsors)],
                ["Digital", (s) => kmp(s.revenue.digital)],
                ["Dinner", (s) => kmp(s.revenue.dinner)],
                ["Revenue", (s) => kmp(s.revenue.total)],
                ["Costs", (s) => kmp(s.costs.total)],
              ].map(([lbl, fn]) => (
                <tr key={lbl}><td>{lbl}</td>{scenKeys.map((k) => <td key={k} className={`r${k === "base" ? " base" : ""}`}>{fn(c.scenarios[k])}</td>)}</tr>
              ))}
              <tr className="tot">
                <td>Profit</td>
                {scenKeys.map((k) => {
                  const p = c.scenarios[k].profit;
                  return <td key={k} className="r" style={{ color: p < 0 ? "var(--neg)" : "var(--pos)" }}>{p < 0 ? "−" : "+"}{kmp(Math.abs(p))}</td>;
                })}
              </tr>
            </tbody>
          </table>
          <p className="text-[12px] mt-4 leading-relaxed" style={{ color: "var(--mut)" }}>
            Most event costs are fixed — falling from {b.physical.sold} to {c.scenarios.downside.physical.sold} paid attendees
            does not reduce costs proportionally. This is why anchor partnerships and pre-sales matter.
          </p>
        </section>

        {/* 05 Break-even & liquidity */}
        <section className="bud-sek bud-reveal" data-testid="bud-cash">
          <Head idx="05" lbl="Break-even & liquidity" />
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bud-stat"><p className="v">{beReq ?? "—"}</p><p className="k">Break-even</p><p className="s">paid tickets · contribution ~{nk(b.breakEven.contribution)}/ticket</p></div>
            <div className="bud-stat"><p className="v" style={{ color: b.cash.fundingRequired ? "var(--neg)" : "var(--pos)" }}>{b.cash.fundingRequired ? kmp(b.cash.fundingRequired.amount) : "None"}</p><p className="k">Peak funding req.</p><p className="s">{b.cash.fundingRequired ? `lowest in ${mnd(b.cash.fundingRequired.month)}` : "positive through the window"}</p></div>
            <div className="bud-stat"><p className="v" style={{ color: b.cash.lowest && b.cash.lowest.amount < 0 ? "var(--neg)" : "var(--pos)" }}>{b.cash.lowest ? kmp(b.cash.lowest.amount) : "—"}</p><p className="k">Lowest cash</p><p className="s">{b.cash.lowest ? mnd(b.cash.lowest.month) : ""} · opening {kmp(b.cash.opening)}</p></div>
          </div>

          {beReq != null && (
            <div className="bud-be">
              <div className="bud-be-spor">
                <span className="bud-be-fyll" style={{ width: `${Math.min(100, (beReq / sellable) * 100)}%` }} />
                <span className="bud-be-mrk" style={{ left: `${Math.min(100, (paid / sellable) * 100)}%` }} title={`Base plan: ${paid} paid`} />
              </div>
              <div className="bud-be-lbl">
                <span>0</span>
                <span style={{ color: "var(--acc)" }}>Break-even {beReq}</span>
                <span style={{ color: "var(--soft2)" }}>Base {paid}</span>
                <span>{sellable} sellable</span>
              </div>
            </div>
          )}

          <div className="bud-cash-graf" data-testid="bud-cash-bars">
            <div className="bud-cash">
              {b.cash.months.map((m) => (
                <div key={m.month} className="stolpe" title={`${m.month}: ${nk(m.closing)}`}>
                  <span className="val">{kmp(m.closing)}</span>
                  <span
                    className={`fyll ${m.closing < 0 ? "neg" : ""} ${m.month === d.eventMonth ? "event" : ""}`}
                    style={{ height: `${Math.max(2, (Math.abs(m.closing) / maxCash) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="bud-cash-mnd">
              {b.cash.months.map((m) => <span key={m.month}>{mnd(m.month)}</span>)}
            </div>
          </div>
          <p className="bud-mono text-[8.5px] uppercase tracking-[0.2em] mt-3" style={{ color: "var(--dim)" }}>
            Closing cash per month · incl. VAT · payment-plan based · <span style={{ color: "var(--acc)" }}>event month</span>
          </p>
        </section>

        {/* 06 Partner architecture */}
        <section className="bud-sek bud-reveal" data-testid="bud-partners">
          <Head idx="06" lbl="Partner architecture" />
          <div className="bud-targets mb-7">
            {(d.partnerTargets || []).map((tg) => (
              <div key={tg.role}>
                <p className="bud-mono bud-tab text-[15px]" style={{ fontWeight: 300, letterSpacing: "-0.03em" }}>{kmp(tg.qty * tg.amount)}</p>
                <p className="text-[9.5px] uppercase leading-snug mt-1.5" style={{ color: "var(--mut)", letterSpacing: "0.08em" }}>{tg.qty > 1 ? `${tg.qty} × ` : ""}{tg.role}</p>
              </div>
            ))}
          </div>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="text-[12px]" style={{ color: "var(--soft)" }}>
              Signed <span style={{ color: "var(--acc)" }}>{kmp(signert)}</span> · weighted pipeline {kmp(vektet)}
            </p>
            <p className="bud-mono bud-tab text-[10px]" style={{ color: "var(--ink)" }}>Target {kmp(targetTotal)}</p>
          </div>
          <div className="bud-bar">
            <span className="vekt" style={{ width: `${Math.min(100, ((signert + vektet) / Math.max(1, targetTotal)) * 100)}%` }} />
            <span className="sign" style={{ width: `${Math.min(100, (signert / Math.max(1, targetTotal)) * 100)}%` }} />
          </div>
          <p className="text-[11.5px] mt-3" style={{ color: "var(--mut)" }}>
            Fewer partners. Stronger positions. No category overlap. Target ≠ committed revenue — forecast is weighted from the real pipeline.
          </p>
        </section>

        {/* 07 Roadmap — fase-status fra Playbook-milestones */}
        {(d.milestones || []).length > 0 && (
          <section className="bud-sek bud-reveal" data-testid="bud-roadmap">
            <Head idx="07" lbl="Roadmap to summit day" />
            <div className="bud-vei">
              {d.milestones.map((m, i) => (
                <div key={i} className={`node ${m.phase}`} data-testid={`bud-vei-${i}`}>
                  <span className="dot" />
                  <p className="nm">{m.t}</p>
                  <p className="ds">{m.d}</p>
                  <p className="ph">
                    {m.phase === "done" ? "Complete ✓" : m.phase === "active" ? "Active phase" : "Upcoming"}
                    {m.total > 0 ? ` · ${m.done}/${m.total}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 08 Key risks */}
        <section className="bud-sek bud-reveal" data-testid="bud-risks">
          <Head idx="08" lbl="Key risks" />
          {(c.risks || []).map((r, i) => (
            <div key={i} className={`bud-risk ${r.tone}`}>
              <span className="tone">{r.tone === "ok" ? "On track" : r.tone}</span>
              <span>{r.text}</span>
            </div>
          ))}
        </section>

        <div className="bud-signoff bud-reveal">
          <span className="wordmark" style={{ fontSize: 11 }}>
            <span className="wordmark__bold">Norwegian</span>
            <span className="wordmark__light">AI Summit</span>
          </span>
          <p className="bud-mono text-[8.5px] uppercase tracking-[0.26em]" style={{ color: "var(--dim)" }}>
            Budget memo · Base Case {mn(b.costs.total)} · Private — not for distribution
          </p>
        </div>
      </div>
    </div>
  );
}

/* Offentlig, passordbeskyttet budsjettrapport. ?pdf=1&rc= → server-side render. */
export default function BudsjettRapport() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const pdfModus = params.get("pdf") === "1";
  const rc = params.get("rc") || "";
  const [lys, setLys] = useState(() => (pdfModus ? params.get("theme") === "light" : localStorage.getItem("nas_budrap_lys") === "1"));
  const byttTema = () => setLys((v) => { localStorage.setItem("nas_budrap_lys", v ? "0" : "1"); return !v; });
  const [status, setStatus] = useState("laster");
  const [eventName, setEventName] = useState("Norwegian AI Summit 2027");
  const [data, setData] = useState(null);
  const [kode, setKode] = useState("");
  const [kodeFeil, setKodeFeil] = useState("");
  const [sjekker, setSjekker] = useState(false);

  useEffect(() => {
    document.title = "Norwegian AI Summit 2027 — Budget";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const stil = document.createElement("style");
    stil.id = "bud-page-stil";
    stil.textContent = "@page { size: A4 portrait; margin: 0; }";
    document.head.appendChild(stil);
    return () => { document.head.removeChild(meta); document.head.removeChild(stil); };
  }, []);

  useEffect(() => {
    let aktiv = true;
    const hent = async () => {
      try {
        if (pdfModus && rc) {
          const r = await api.post(`/budget/s/${token}/unlock`, { rc });
          if (!aktiv) return;
          setData(r.data.data);
          setStatus("aapen");
          return;
        }
        const r = await api.get(`/budget/s/${token}`);
        if (!aktiv) return;
        setEventName(r.data.eventName || "Norwegian AI Summit 2027");
        setStatus("laast");
      } catch {
        if (aktiv) setStatus("borte");
      }
    };
    hent();
    return () => { aktiv = false; };
  }, [token, pdfModus, rc]);

  const laasOpp = async (e) => {
    e?.preventDefault();
    if (!kode.trim() || sjekker) return;
    setSjekker(true);
    setKodeFeil("");
    try {
      const r = await api.post(`/budget/s/${token}/unlock`, { code: kode.trim().toUpperCase() });
      setData(r.data.data);
      setStatus("aapen");
    } catch (err) {
      setKodeFeil(err?.response?.status === 403 ? "Wrong access code — try again." : "Something went wrong. Please try again.");
    } finally {
      setSjekker(false);
    }
  };

  if (status === "aapen" && data) {
    return (
      <Rapport
        d={data}
        lys={lys}
        chrome={!pdfModus ? (
          <div className="bud-verktoy bud-chrome">
            <button type="button" className="bud-print-btn" onClick={byttTema} data-testid="bud-tema-toggle">
              {lys ? <><Moon size={11} /> Dark</> : <><Sun size={11} /> Light</>}
            </button>
            <button type="button" className="bud-print-btn" onClick={() => window.print()} data-testid="bud-print">
              <Printer size={11} /> Print / PDF
            </button>
          </div>
        ) : null}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#070a0e] flex items-center justify-center px-5 text-center">
      {status === "laster" && (
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#565c66]">Loading …</p>
      )}
      {status === "borte" && (
        <div data-testid="bud-borte">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#565c66]">Norwegian AI Summit 2027</p>
          <p className="text-[18px] font-semibold text-[#f5f1e8] mt-3">This link is no longer active.</p>
        </div>
      )}
      {status === "laast" && (
        <div className="w-full max-w-[380px]" data-testid="bud-gate">
          <span className="wordmark justify-center" style={{ fontSize: 12 }}>
            <span className="wordmark__bold">Norwegian</span>
            <span className="wordmark__light">AI Summit</span>
          </span>
          <p className="font-mono text-[9px] uppercase tracking-[0.34em] text-[#565c66] mt-6">Budget memo — Private</p>
          <h1 className="text-[26px] font-bold uppercase tracking-[-0.01em] text-[#f5f1e8] mt-3" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}>
            The Economics
          </h1>
          <p className="text-[12.5px] text-[#8b9199] mt-2">{eventName}</p>
          <form onSubmit={laasOpp} className="mt-8">
            <input
              value={kode}
              onChange={(e) => { setKode(e.target.value.toUpperCase()); setKodeFeil(""); }}
              placeholder="ACCESS CODE"
              autoFocus
              maxLength={12}
              className="w-full bg-[#0a0e13] border border-white/[0.14] rounded-[2px] px-3 py-3 text-center font-mono text-[15px] tracking-[0.4em] uppercase text-[#f5f1e8] placeholder:text-[#3a4048] focus:outline-none focus:border-[#F1E75A]/60 transition-colors"
              data-testid="bud-kode"
            />
            {kodeFeil && <p className="text-[11.5px] text-red-400 mt-2.5" data-testid="bud-feil">{kodeFeil}</p>}
            <button
              type="submit"
              disabled={!kode.trim() || sjekker}
              className="w-full mt-4 bg-white text-[#070a0e] text-[13px] font-semibold rounded-[2px] px-4 py-3 hover:bg-[#f1e75a] transition-colors disabled:opacity-50"
              data-testid="bud-aapne"
            >
              {sjekker ? "Checking …" : "Open budget"}
            </button>
          </form>
          <p className="font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#3a4048] mt-7">Shared personally · not for distribution</p>
        </div>
      )}
    </div>
  );
}
